import React, { useState } from "react";
import { Alert, Badge, ProgressBar } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { useAppSelector } from "pages/thermography/config/store";
import {
    selectImportState,
    selectImportsByFile,
    selectWizardFiles
} from "../dataImportWizardSlice";
import { useTranslation } from "react-i18next";

export default function ImportStep() {
    const { t } = useTranslation();
    const tt = (key: string, options?: Record<string, unknown>) => t(key, { ns: "translation", ...options });
    const files = useAppSelector(selectWizardFiles);
    const importsByFile = useAppSelector(selectImportsByFile);
    const importState = useAppSelector(selectImportState);

    const [open, setOpen] = useState<Record<string, boolean>>({});

    const statusBadge = (fileId: string) => {
        const imp = importsByFile[fileId];
        if (!imp || imp.status === "idle") return <Badge bg="secondary">{tt("pages.thermography.dataImportWizard.import.status.waiting")}</Badge>;
        if (imp.status === "running") return <Badge bg="info">{tt("pages.thermography.dataImportWizard.import.status.importing")}</Badge>;
        if (imp.status === "failed") return <Badge bg="danger">{tt("pages.thermography.dataImportWizard.import.status.failed")}</Badge>;
        if (imp.status === "succeeded") {
            return imp.result?.success ? <Badge bg="success">{tt("pages.thermography.dataImportWizard.import.status.imported")}</Badge> : <Badge bg="danger">{tt("pages.thermography.dataImportWizard.import.status.failed")}</Badge>;
        }
        return <Badge bg="secondary">{tt("pages.thermography.dataImportWizard.import.status.waiting")}</Badge>;
    };

    return (
        <div>
            <h5 className="mb-2">{tt("pages.thermography.dataImportWizard.import.title")}</h5>
            <p className="text-muted mb-3">
                {tt("pages.thermography.dataImportWizard.import.description")}
            </p>

            {!files.length && (
                <Alert variant="subtle-warning">
                    {tt("pages.thermography.dataImportWizard.import.noFiles")}
                </Alert>
            )}

            {files.map(f => {
                const imp = importsByFile[f.id];
                const isOpen = !!open[f.id];

                const message =
                    imp?.status === "failed"
                        ? (imp.error ?? imp.result?.message ?? tt("pages.thermography.dataImportWizard.import.defaultFailureMessage"))
                        : (imp?.result?.message ?? (imp?.status === "running" ? tt("pages.thermography.dataImportWizard.import.defaultImportingMessage") : tt("pages.thermography.dataImportWizard.import.defaultWaitingMessage")));

                return (
                    <div key={f.id} className="border rounded bg-body mb-3">
                        <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
                            <div className="d-flex align-items-center gap-2">
                                <button
                                    type="button"
                                    className="btn btn-sm p-0"
                                    onClick={() => setOpen(prev => ({ ...prev, [f.id]: !prev[f.id] }))}
                                    aria-label={tt("pages.thermography.dataImportWizard.common.expandCollapse")}
                                >
                                    <FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} />
                                </button>

                                <div className="fw-semibold">{f.name}</div>
                            </div>

                            <div className="d-flex align-items-center gap-2">
                                {statusBadge(f.id)}
                            </div>
                        </div>

                        {imp?.status === "running" && (
                            <div className="px-3 py-2">
                                <ProgressBar now={imp.progress} />
                            </div>
                        )}

                        {isOpen && (
                            <div className="p-3">
                                {imp?.status === "failed" ? (
                                    <Alert variant="subtle-danger" className="py-2 mb-0">{message}</Alert>
                                ) : imp?.result?.success ? (
                                    <Alert variant="subtle-success" className="py-2 mb-0">{message}</Alert>
                                ) : (
                                    <Alert variant="subtle-info" className="py-2 mb-0">{message}</Alert>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}

            {/* Barra única geral abaixo dos botões */}
            {importState.status === "running" && (
                <div className="mt-2">
                    <ProgressBar now={importState.progress} />
                </div>
            )}

            {importState.status === "failed" && (
                <div className="mt-2">
                    <Alert variant="subtle-danger" className="py-2 mb-0">
                        {importState.error ?? tt("pages.thermography.dataImportWizard.import.someFilesNotImported")}
                    </Alert>
                </div>
            )}
        </div>
    );
}
